import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, Filter, LayoutGrid, Pencil, Table2, X } from 'lucide-react';
import type { MasterInventoryItem, QuoteType, ResidentialItemType } from '../../types/admin';
import { CustomDropdown } from '../ui/CustomDropdown';
import { isPricedCategory, parseRentalDaysInput, parseUnitCostInput } from '../../lib/inventoryPricing';
import { CATEGORY_LABELS, type CategoryFilter, type InventoryFilters } from './InventoryFilterSheet';

/**
 * Tabular view shared by the master and org inventory pages.
 *
 * Read-only grid; click a value to edit it in place. Enter, the check button
 * or clicking away commits (dropdowns commit as soon as an option is picked);
 * Escape or the X button reverts. The name column and the header row stay
 * pinned while the grid scrolls.
 *
 * Add Equipment is not edited in place: clicking it opens the full edit
 * modal scrolled to that section.
 */

export type InventoryView = 'cards' | 'table';
export const INVENTORY_VIEW_KEY = 'iomi_inventory_view';

export function readStoredInventoryView(): InventoryView {
  try {
    return localStorage.getItem(INVENTORY_VIEW_KEY) === 'table' ? 'table' : 'cards';
  } catch {
    return 'cards';
  }
}

export function storeInventoryView(view: InventoryView): void {
  try {
    localStorage.setItem(INVENTORY_VIEW_KEY, view);
  } catch {
    /* private mode or blocked storage: the choice just does not persist */
  }
}

/** Cards / table switch. Sits beside the Commercial / Residential toggle and matches its height. */
export function InventoryViewSwitch({ value, onChange }: { value: InventoryView; onChange: (v: InventoryView) => void }) {
  return (
    <div className="flex bg-mist rounded-lg p-0.5 gap-0.5 md:p-1 shrink-0" role="group" aria-label="List layout">
      {([
        { value: 'cards', label: 'Card view', Icon: LayoutGrid },
        { value: 'table', label: 'Table view', Icon: Table2 },
      ] as const).map(({ value: v, label, Icon }) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          aria-label={label}
          aria-pressed={value === v}
          title={label}
          className={`h-full min-h-[44px] md:min-h-0 w-11 flex items-center justify-center rounded-md transition-all duration-150
            ${value === v ? 'bg-white text-midnight shadow-brand-sm' : 'text-slate hover:text-midnight'}`}
        >
          <Icon size={16} />
        </button>
      ))}
    </div>
  );
}

/** Fields the table can change in place. Mirrors the PATCH body the pages already send. */
export interface InventoryTablePatch {
  name?: string;
  type?: MasterInventoryItem['type'];
  residentialType?: ResidentialItemType | null;
  unitCost?: number | null;
  rentalDays?: number | null;
  kz?: number;
  additionalManHours?: number | null;
  isAccessorial?: boolean;
  accessorialLabel?: string;
}

interface InventoryTableProps<T extends MasterInventoryItem> {
  items: T[];
  viewMode: QuoteType;
  /** Commercial item types offered in the Type dropdown. */
  typeOptions: readonly string[];
  onUpdate: (item: T, patch: InventoryTablePatch) => Promise<unknown>;
  /** Open the full edit modal; `section` asks it to scroll to that part. */
  onEdit: (item: T, section?: 'addEquipment') => void;
  /** Page-level filters: the Type and Category header menus drive these. */
  filters: InventoryFilters;
  onFiltersChange: (f: InventoryFilters) => void;
}

/** Table-only yes / no filters for the header menus. */
type YesNo = 'all' | 'yes' | 'no';
interface FlagFilters {
  hours: YesNo;
  accessorial: YesNo;
  equipment: YesNo;
}
const NO_FLAG_FILTERS: FlagFilters = { hours: 'all', accessorial: 'all', equipment: 'all' };
const YES_NO_OPTIONS: { value: YesNo; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

function hasHours(item: MasterInventoryItem): boolean {
  return item.additionalManHours != null && item.additionalManHours > 0;
}
function hasEquipment(item: MasterInventoryItem): boolean {
  return !!item.addEquipment && item.addEquipment.length > 0;
}
function matchesYesNo(f: YesNo, value: boolean): boolean {
  return f === 'all' || (f === 'yes') === value;
}

// Category is required, so there is no "None" choice.
const CATEGORY_OPTIONS: { value: ResidentialItemType; label: string }[] = [
  { value: 'customer_item', label: CATEGORY_LABELS.customer_item },
  { value: 'packing_item', label: CATEGORY_LABELS.packing_item },
  { value: 'equipment', label: CATEGORY_LABELS.equipment },
];

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function formatHours(h: number): string {
  return `${h} hr${h === 1 ? '' : 's'}`;
}

function formatDays(d: number): string {
  return `${d} day${d === 1 ? '' : 's'}`;
}

function formatEquipment(item: MasterInventoryItem): string | null {
  if (!item.addEquipment || item.addEquipment.length === 0) return null;
  return item.addEquipment.map(e => `${e.quantity}× ${e.itemName}`).join(', ');
}

/** Runs `onOutside` on a pointer-down outside `ref` while `active`. */
function useOutsideClick(ref: React.RefObject<HTMLElement>, active: boolean, onOutside: () => void) {
  useEffect(() => {
    if (!active) return;
    function handle(e: PointerEvent) {
      const el = ref.current;
      if (el && !el.contains(e.target as Node)) onOutside();
    }
    document.addEventListener('pointerdown', handle);
    return () => document.removeEventListener('pointerdown', handle);
  }, [ref, active, onOutside]);
}

// ── Cell editor ────────────────────────────────────────────────────────────

type CellKind = 'text' | 'money' | 'number' | 'select';

interface EditableCellProps {
  kind: CellKind;
  /** Current value as a string ('' = blank). */
  value: string;
  display: ReactNode;
  /** When false the value renders plain and cannot be edited. */
  editable: boolean;
  /** Dropdown options for `kind === 'select'`. */
  options?: { value: string; label: string }[];
  /** Return an error message to block the commit. */
  validate?: (next: string) => string | null;
  /** Called only when the value changed and validated. */
  onCommit: (next: string) => Promise<unknown>;
  /** Accessible name of the cell, e.g. "KZ for Desk". */
  label: string;
  align?: 'left' | 'right';
  /** Input step / inputMode hints for number cells. */
  step?: string;
}

function EditableCell({ kind, value, display, editable, options, validate, onCommit, label, align = 'left', step }: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const dropdownId = useId();

  // Keep the draft in sync when the row re-renders with a new value while idle.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // Clicking anywhere outside the editor saves (an invalid value stays open with its message); Escape reverts.
  useOutsideClick(wrapRef, editing && !saving, () => { void commit(); });

  // Dropdown editors have no autoFocus of their own: focus the trigger so Escape works.
  useEffect(() => {
    if (editing && kind === 'select') document.getElementById(dropdownId)?.focus();
  }, [editing, kind, dropdownId]);

  function start() {
    if (!editable) return;
    setDraft(value);
    setError('');
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setError('');
    setDraft(value);
  }

  async function commit(override?: string) {
    if (saving) return;
    const next = (override ?? draft).trim();
    if (next === value) { setEditing(false); return; }
    const message = validate?.(next) ?? null;
    if (message) { setError(message); return; }
    setSaving(true);
    try {
      await onCommit(next);
      setEditing(false);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  function handleKey(e: KeyboardEvent<HTMLElement>) {
    // Enter on a dropdown trigger opens the menu; dropdown cells save as soon as an option is picked.
    if (e.key === 'Enter' && kind !== 'select') { e.preventDefault(); void commit(); }
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  }

  if (!editing) {
    if (!editable) {
      return <span className={`block px-2 py-1.5 text-slate ${align === 'right' ? 'text-right' : ''}`}>{display}</span>;
    }
    return (
      <button
        type="button"
        onClick={start}
        className={`block w-full min-h-[36px] px-2 py-1.5 rounded-md text-left
                    hover:bg-plum/5 hover:ring-1 hover:ring-inset hover:ring-plum/30
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-plum/40
                    transition-colors duration-100 ${align === 'right' ? 'text-right' : ''}`}
        aria-label={`Edit ${label}`}
        title="Click to edit"
      >
        {display}
      </button>
    );
  }

  const inputClass = `h-9 px-2 rounded-lg border border-plum bg-white text-[13px] text-midnight
                      focus:outline-none focus:ring-2 focus:ring-plum/20
                      ${kind === 'text' ? 'w-44' : 'w-24 font-mono text-right'}`;

  return (
    <div ref={wrapRef} className="px-1 py-1" onKeyDown={handleKey}>
      <div className="flex items-center gap-1">
        {kind === 'select' ? (
          <div className="w-44">
            <CustomDropdown
              id={dropdownId}
              value={draft}
              options={options ?? []}
              onChange={v => { setDraft(v); setError(''); void commit(v); }}
            />
          </div>
        ) : (
          <input
            type={kind === 'text' ? 'text' : 'number'}
            inputMode={kind === 'text' ? undefined : 'decimal'}
            min={kind === 'text' ? undefined : 0}
            step={step ?? (kind === 'money' ? '0.01' : kind === 'number' ? 'any' : undefined)}
            value={draft}
            onChange={e => { setDraft(e.target.value); setError(''); }}
            autoFocus
            disabled={saving}
            aria-label={label}
            className={inputClass}
          />
        )}
        <button
          type="button"
          onClick={() => void commit()}
          disabled={saving}
          className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg bg-plum text-white
                     hover:bg-rose disabled:opacity-50 transition-colors"
          aria-label={`Save ${label}`}
        >
          <Check size={14} />
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg border border-stone text-slate
                     hover:text-midnight hover:border-plum/40 disabled:opacity-50 transition-colors"
          aria-label={`Cancel editing ${label}`}
        >
          <X size={14} />
        </button>
      </div>
      {error && <p className="mt-1 text-[11px] text-rose leading-snug max-w-[220px]">{error}</p>}
    </div>
  );
}

// ── Accessorial cell (flag + label in one editor) ──────────────────────────

function AccessorialCell({ item, onCommit }: {
  item: MasterInventoryItem;
  onCommit: (patch: InventoryTablePatch) => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [flag, setFlag] = useState(!!item.isAccessorial);
  const [label, setLabel] = useState(item.accessorialLabel ?? '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  // Clicking away saves (a missing label keeps the editor open with its message); Escape reverts.
  useOutsideClick(wrapRef, editing && !saving, () => { void commit(); });

  function start() {
    setFlag(!!item.isAccessorial);
    setLabel(item.accessorialLabel ?? '');
    setError('');
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setError('');
  }

  async function commit() {
    if (saving) return;
    const trimmed = label.trim();
    if (flag === !!item.isAccessorial && (!flag || trimmed === (item.accessorialLabel ?? ''))) {
      setEditing(false); return;
    }
    if (flag && !trimmed) { setError('Accessorial label is required when accessorial service is enabled.'); return; }
    setSaving(true);
    try {
      await onCommit(flag ? { isAccessorial: true, accessorialLabel: trimmed } : { isAccessorial: false });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  function handleKey(e: KeyboardEvent<HTMLElement>) {
    if (e.key === 'Enter') { e.preventDefault(); void commit(); }
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={start}
        className="block w-full min-h-[36px] px-2 py-1.5 rounded-md text-left
                   hover:bg-plum/5 hover:ring-1 hover:ring-inset hover:ring-plum/30
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-plum/40
                   transition-colors duration-100"
        aria-label={`Edit accessorial service for ${item.name}`}
        title="Click to edit"
      >
        {item.isAccessorial ? (
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-plum/10 text-plum">Yes</span>
            <span className="truncate text-midnight">{item.accessorialLabel}</span>
            <span className="shrink-0 font-mono text-[11px] text-slate">
              {item.accessorialOriginManHours ?? 0}h / {item.accessorialDestinationManHours ?? 0}h
            </span>
          </span>
        ) : (
          <span className="text-slate">—</span>
        )}
      </button>
    );
  }

  return (
    <div ref={wrapRef} className="px-1 py-1" onKeyDown={handleKey}>
      <div className="flex items-center gap-1.5">
        <label className="flex items-center gap-1.5 h-9 px-2 rounded-lg border border-plum bg-white cursor-pointer select-none">
          <input
            type="checkbox"
            checked={flag}
            onChange={e => { setFlag(e.target.checked); setError(''); }}
            autoFocus
            className="w-4 h-4 accent-plum"
            aria-label={`Accessorial service for ${item.name}`}
          />
          <span className="text-[13px] text-midnight">Accessorial</span>
        </label>
        {flag && (
          <input
            type="text"
            value={label}
            onChange={e => { setLabel(e.target.value); setError(''); }}
            placeholder="Label, e.g. Glass Bonding"
            disabled={saving}
            aria-label={`Accessorial label for ${item.name}`}
            className="h-9 w-40 px-2 rounded-lg border border-plum bg-white text-[13px] text-midnight
                       placeholder:text-slate focus:outline-none focus:ring-2 focus:ring-plum/20"
          />
        )}
        <button
          type="button"
          onClick={() => void commit()}
          disabled={saving}
          className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg bg-plum text-white
                     hover:bg-rose disabled:opacity-50 transition-colors"
          aria-label={`Save accessorial service for ${item.name}`}
        >
          <Check size={14} />
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg border border-stone text-slate
                     hover:text-midnight hover:border-plum/40 disabled:opacity-50 transition-colors"
          aria-label={`Cancel editing accessorial service for ${item.name}`}
        >
          <X size={14} />
        </button>
      </div>
      {error && <p className="mt-1 text-[11px] text-rose leading-snug max-w-[260px]">{error}</p>}
    </div>
  );
}

// ── Column header with a filter menu ───────────────────────────────────────

interface HeaderFilterProps<V extends string> {
  label: string;
  value: V;
  options: ReadonlyArray<{ value: V; label: string }>;
  onChange: (v: V) => void;
  align?: 'left' | 'right';
}

/**
 * Header cell with a funnel button. The menu is portalled to <body> and
 * positioned from the button's rect, so the table's scroll container can't
 * clip it. Closes on outside click, Escape, or when the table scrolls.
 */
function HeaderFilter<V extends string>({ label, value, options, onChange, align = 'left' }: HeaderFilterProps<V>) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; right?: number } | null>(null);
  const active = value !== 'all';
  const current = options.find(o => o.value === value)?.label;

  function place() {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const menuWidth = 176;
    const left = Math.min(Math.max(8, r.left), window.innerWidth - menuWidth - 8);
    setPos({ top: r.bottom + 4, left });
  }

  useLayoutEffect(() => {
    if (open) place();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointer(e: PointerEvent) {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: globalThis.KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    // Focusing a half-hidden header button scrolls the grid, so follow the button instead of closing.
    function onScroll() { place(); }
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  return (
    <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
      <span className={active ? 'text-plum' : ''}>{label}</span>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Filter by ${label.toLowerCase()}${active ? `: ${current}` : ''}`}
        title={active ? `${label}: ${current}` : `Filter by ${label.toLowerCase()}`}
        className={`h-6 w-6 -my-1 flex items-center justify-center rounded-md transition-colors
          ${active ? 'bg-plum text-white' : 'text-slate hover:text-midnight hover:bg-stone/60'}`}
      >
        <Filter size={11} />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label={`${label} filter`}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: 176 }}
          className="z-[60] bg-white border border-stone rounded-xl shadow-brand-md p-1 normal-case tracking-normal"
        >
          {options.map(opt => {
            const selected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`w-full h-9 px-2.5 rounded-lg flex items-center justify-between gap-2 text-[13px] font-medium text-left
                  ${selected ? 'bg-plum/5 text-plum' : 'text-midnight hover:bg-mist'}`}
              >
                <span className="truncate">{opt.label}</span>
                {selected && <Check size={13} className="shrink-0" />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

// ── Table ──────────────────────────────────────────────────────────────────

const TH_BASE = 'sticky top-0 bg-mist px-2 py-2 text-[11px] font-bold uppercase tracking-widest text-slate whitespace-nowrap border-b border-stone';
const TH = `${TH_BASE} z-10`;
/* Solid hover colour so the pinned name cell keeps hiding what scrolls beneath it. */
const TD = 'px-0 py-0 text-[13px] text-midnight whitespace-nowrap border-b border-stone align-middle bg-white group-hover:bg-mist';

export function InventoryTable<T extends MasterInventoryItem>({ items, viewMode, typeOptions, onUpdate, onEdit, filters, onFiltersChange }: InventoryTableProps<T>) {
  const isCommercial = viewMode === 'commercial';
  const [flags, setFlags] = useState<FlagFilters>(NO_FLAG_FILTERS);
  const flagCount = Object.values(flags).filter(v => v !== 'all').length;

  // Type / category are already applied by the page; the yes/no filters are applied here.
  const rows = items.filter(item =>
    (!isCommercial || (matchesYesNo(flags.hours, hasHours(item)) && matchesYesNo(flags.accessorial, !!item.isAccessorial)))
    && matchesYesNo(flags.equipment, hasEquipment(item)),
  );
  const columnCount = isCommercial ? 10 : 6;

  return (
    <div
      className="overflow-auto max-h-[calc(100dvh-230px)] md:max-h-[calc(100vh-260px)]
                 rounded-xl border border-stone bg-white shadow-brand-sm"
      role="region"
      aria-label="Inventory table"
    >
      <table className="min-w-full border-separate border-spacing-0 text-left">
        <thead>
          <tr>
            <th scope="col" className={`${TH_BASE} left-0 z-20 min-w-[120px] max-w-[150px] md:min-w-[160px] md:max-w-[220px]`}>Name</th>
            {isCommercial && (
              <th scope="col" className={TH}>
                <HeaderFilter
                  label="Type"
                  value={filters.typeFilter}
                  options={[{ value: 'all', label: 'All Types' }, ...typeOptions.map(t => ({ value: t, label: t }))]}
                  onChange={v => onFiltersChange({ ...filters, typeFilter: v })}
                />
              </th>
            )}
            <th scope="col" className={TH}>
              <HeaderFilter<CategoryFilter>
                label="Category"
                value={filters.categoryFilter}
                options={[
                  { value: 'all', label: 'All Categories' },
                  { value: 'customer_item', label: CATEGORY_LABELS.customer_item },
                  { value: 'packing_item', label: CATEGORY_LABELS.packing_item },
                  { value: 'equipment', label: CATEGORY_LABELS.equipment },
                ]}
                onChange={v => onFiltersChange({ ...filters, categoryFilter: v })}
              />
            </th>
            <th scope="col" className={`${TH} text-right`}>Cost</th>
            <th scope="col" className={`${TH} text-right`}>Rental</th>
            {isCommercial && <th scope="col" className={`${TH} text-right`}>KZ</th>}
            {isCommercial && (
              <th scope="col" className={`${TH} text-right`}>
                <HeaderFilter label="Add'l Hours" align="right" value={flags.hours} options={YES_NO_OPTIONS}
                  onChange={v => setFlags(f => ({ ...f, hours: v }))} />
              </th>
            )}
            {isCommercial && (
              <th scope="col" className={TH}>
                <HeaderFilter label="Accessorial" value={flags.accessorial} options={YES_NO_OPTIONS}
                  onChange={v => setFlags(f => ({ ...f, accessorial: v }))} />
              </th>
            )}
            <th scope="col" className={TH}>
              <HeaderFilter label="Add Equipment" value={flags.equipment} options={YES_NO_OPTIONS}
                onChange={v => setFlags(f => ({ ...f, equipment: v }))} />
            </th>
            <th scope="col" className={`${TH} text-right`}><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={columnCount} className="px-4 py-8 text-center text-small text-slate">
                No items match the column filters.
                {flagCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setFlags(NO_FLAG_FILTERS)}
                    className="ml-2 font-semibold text-plum hover:text-rose"
                  >
                    Clear yes / no filters
                  </button>
                )}
              </td>
            </tr>
          )}
          {rows.map(item => {
            const priced = isPricedCategory(item.residentialType);
            const equipment = formatEquipment(item);
            const accessorial = !!item.isAccessorial;
            return (
              <tr key={item.id} className="group">
                {/* Name — pinned */}
                <th scope="row" className={`${TD} sticky left-0 z-10 font-semibold min-w-[120px] max-w-[150px] md:min-w-[160px] md:max-w-[220px]
                                              shadow-[inset_-1px_0_0_#E8E3EA]`}>
                  <EditableCell
                    kind="text"
                    value={item.name}
                    display={<span className="block truncate" title={item.name}>{item.name}</span>}
                    editable
                    label={`name for ${item.name}`}
                    validate={v => (v ? null : 'Name is required.')}
                    onCommit={v => onUpdate(item, { name: v })}
                  />
                </th>

                {isCommercial && (
                  <td className={TD}>
                    <EditableCell
                      kind="select"
                      value={item.type}
                      display={item.type}
                      editable
                      options={typeOptions.map(t => ({ value: t, label: t }))}
                      label={`type for ${item.name}`}
                      onCommit={v => onUpdate(item, { type: v as MasterInventoryItem['type'] })}
                    />
                  </td>
                )}

                <td className={TD}>
                  <EditableCell
                    kind="select"
                    value={item.residentialType ?? 'customer_item'}
                    display={CATEGORY_LABELS[item.residentialType ?? 'customer_item']}
                    editable
                    options={CATEGORY_OPTIONS}
                    label={`category for ${item.name}`}
                    onCommit={v => onUpdate(item, { residentialType: v as ResidentialItemType })}
                  />
                </td>

                <td className={`${TD} font-mono`}>
                  <EditableCell
                    kind="money"
                    align="right"
                    value={item.unitCost != null ? String(item.unitCost) : ''}
                    display={item.unitCost != null ? currency.format(item.unitCost) : <span className="text-slate">—</span>}
                    editable={priced}
                    label={`unit cost for ${item.name}`}
                    validate={v => {
                      if (v && parseUnitCostInput(v) === null) return 'Enter a cost of $0.00 or more.';
                      if (!v && item.rentalDays != null) return 'Unit cost is required when a rental length is set.';
                      return null;
                    }}
                    onCommit={v => onUpdate(item, { unitCost: parseUnitCostInput(v) })}
                  />
                </td>

                <td className={`${TD} font-mono`}>
                  <EditableCell
                    kind="number"
                    step="1"
                    align="right"
                    value={item.rentalDays != null ? String(item.rentalDays) : ''}
                    display={item.rentalDays != null ? formatDays(item.rentalDays) : <span className="text-slate">—</span>}
                    editable={priced}
                    label={`rental length for ${item.name}`}
                    validate={v => {
                      if (v && parseRentalDaysInput(v) === null) return 'Enter a whole number of days (1 or more).';
                      if (v && item.unitCost == null) return 'Unit cost is required when a rental length is set.';
                      return null;
                    }}
                    onCommit={v => onUpdate(item, { rentalDays: parseRentalDaysInput(v) })}
                  />
                </td>

                {isCommercial && (
                  <td className={`${TD} font-mono`}>
                    <EditableCell
                      kind="number"
                      align="right"
                      value={String(item.kz ?? 0)}
                      display={item.kz ?? 0}
                      editable
                      label={`KZ for ${item.name}`}
                      validate={v => {
                        if (v === '') return null; // optional: blank saves as 0
                        const n = parseFloat(v);
                        return isNaN(n) || n < 0 ? 'KZ must be a valid non-negative number.' : null;
                      }}
                      onCommit={v => onUpdate(item, { kz: v === '' ? 0 : parseFloat(v) })}
                    />
                  </td>
                )}

                {isCommercial && (
                  <td className={`${TD} font-mono`}>
                    <EditableCell
                      kind="number"
                      align="right"
                      value={item.additionalManHours != null ? String(item.additionalManHours) : ''}
                      display={
                        accessorial
                          ? <span className="text-slate" title="Not used while the item has an accessorial service">n/a</span>
                          : item.additionalManHours != null ? `+${formatHours(item.additionalManHours)}` : <span className="text-slate">—</span>
                      }
                      editable={!accessorial}
                      label={`additional man hours for ${item.name}`}
                      validate={v => {
                        if (!v) return null;
                        const n = parseFloat(v);
                        return isNaN(n) || n < 0 ? 'Additional Man Hours must be a valid non-negative number.' : null;
                      }}
                      onCommit={v => onUpdate(item, { additionalManHours: v ? parseFloat(v) : null })}
                    />
                  </td>
                )}

                {isCommercial && (
                  <td className={`${TD} max-w-[260px]`}>
                    <AccessorialCell item={item} onCommit={patch => onUpdate(item, patch)} />
                  </td>
                )}

                <td className={`${TD} max-w-[260px]`}>
                  <button
                    type="button"
                    onClick={() => onEdit(item, 'addEquipment')}
                    className="block w-full min-h-[36px] px-2 py-1.5 rounded-md text-left truncate
                               hover:bg-plum/5 hover:ring-1 hover:ring-inset hover:ring-plum/30
                               focus:outline-none focus-visible:ring-2 focus-visible:ring-plum/40
                               transition-colors duration-100"
                    aria-label={`Edit add equipment for ${item.name}`}
                    title={equipment ?? 'Opens the item editor at Add Equipment'}
                  >
                    {equipment ? <span className="text-midnight">{equipment}</span> : <span className="text-slate">—</span>}
                  </button>
                </td>

                <td className={`${TD} text-right`}>
                  <div className="px-2 py-1">
                    <button
                      type="button"
                      onClick={() => onEdit(item)}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-stone
                                 text-slate hover:border-plum hover:text-plum active:bg-mist
                                 transition-colors duration-150"
                      aria-label={`Edit ${item.name}`}
                    >
                      <Pencil size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
